<template>
  <div>
    <h2 class="mb-5">Introduction to Calorimetry: Data Processing</h2>

    <p class="mb-3">
      a) Please fill in the table below with the titration data and calculations used for the
      determination of
      <stemble-latex content="$\ce{[H2O2]}.$" />
    </p>

    <p class="pl-n8 mb-5">
      <v-simple-table>
        <thead>
          <tr>
            <td style="font-weight: bold; text-align: left; width: 25%">Property</td>
            <td style="font-weight: bold; text-align: center; width: 25%">Trial 1</td>
            <td style="font-weight: bold; text-align: center; width: 25%">Trial 2</td>
            <td style="font-weight: bold; text-align: center; width: 25%">Trial 3</td>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>Volume of 3% H2O2 (mL)</td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.volH2O2T1"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.volH2O2T2"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.volH2O2T3"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Final Buret Reading (mL)</td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.VfT1"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.VfT2"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.VfT3"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Initial Buret Reading (mL)</td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.ViT1"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.ViT2"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.ViT3"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Volume of <chemical-latex content="KMnO4" /> used (mL)</td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.volTitrantT1"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.volTitrantT2"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.volTitrantT3"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Notes</td>
            <td class="py-2 px-2 mx-0">
              <v-textarea
                v-model="inputs.notesT1"
                rows="4"
                autogrow
                outlined
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0">
              <v-textarea
                v-model="inputs.notesT2"
                rows="4"
                autogrow
                outlined
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0">
              <v-textarea
                v-model="inputs.notesT3"
                rows="4"
                autogrow
                outlined
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td><chemical-latex content="[H2O2] (M)" /></td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.concH2O2T1"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.concH2O2T2"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.concH2O2T3"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </p>

    <p class="mb-3 mt-4">
      b) What is the mean and standard deviation for <stemble-latex content="$\ce{[H2O2]}?$" />
    </p>

    <v-row class="mb-5">
      <v-col cols="5">
        <calculation-input
          v-model="inputs.meanConcH2O2"
          prepend-text="$\ce{[H2O2]:}$"
          :disabled="!allowEditing"
        />
      </v-col>
      <v-col cols="1" class="pr-n3 mr-n5 pt-2 mt-5">
        <stemble-latex content="$\pm$" />
      </v-col>
      <v-col cols="3">
        <calculation-input
          v-model="inputs.stDevConcH2O2"
          append-text="$\text{M}$"
          :disabled="!allowEditing"
        />
      </v-col>
    </v-row>

    <p class="mb-3">
      c) Please fill in the table below with the calorimetry data and calculations used for the
      determination of
      <stemble-latex content="$\Delta\text{H}~(\text{kJ/mol}).$" />
    </p>

    <p class="pl-n8 mb-5">
      <v-simple-table>
        <thead>
          <tr>
            <td style="font-weight: bold; text-align: left">Property</td>
            <td style="font-weight: bold; text-align: center">Value</td>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>Initial Temp <stemble-latex content="$(^\circ\text{C})$" /></td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.Tinit"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Final Temp <stemble-latex content="$(^\circ\text{C})$" /></td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.Tfinal"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td><stemble-latex content="$\Delta\text{T}~(^\circ\text{C})$" /></td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.deltaT"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Volume of <stemble-latex content="$\ce{H2O2}$" /> used (mL)</td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.volH2O2inRxn"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Initial mass of calorimeter (g)</td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.massCalInit"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Final mass of calorimeter (g)</td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.massCalFinal"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Mass of solution in calorimeter (g)</td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.massSoln"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td><stemble-latex content="$\text{q}_\text{soln}~(\text{J})$" /></td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.qSoln"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td><stemble-latex content="$\text{q}_\text{cal}~(\text{J})$" /></td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.qCal"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td><stemble-latex content="$\text{q}_\text{total}~(\text{J})$" /></td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.qTotal"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td>Moles of <stemble-latex content="$\ce{H2O2}$" /></td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.molH2O2"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>

          <tr>
            <td><stemble-latex content="$\Delta\text{H}~(\text{kJ/mol})$" /></td>
            <td class="py-2 px-2 mx-0 centered-input">
              <calculation-input
                v-model="inputs.deltaH"
                class="centered-input"
                :disabled="!allowEditing"
              />
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </p>

    <p class="mb-3">
      d) What is the percent error for the value of
      <stemble-latex content="$\Delta\text{H}~(\text{kJ/mol})$" /> you determined in the experiment?
    </p>

    <calculation-input
      v-model="inputs.pctError"
      prepend-text="$\text{Percent Error:}$"
      append-text="$\%$"
      class="mb-3"
      :disabled="!allowEditing"
    />
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import StembleLatex from '@/tasks/components/StembleLatex';
import STextarea from '@/common/components/STextarea.vue';
import CalculationInput from '@/tasks/components/inputs/CalculationInput.vue';
import ChemicalLatex from '@/tasks/components/displayers/ChemicalLatex.vue';

export default {
  name: 'UNO_Lab13_ReportSheet',
  components: {
    ChemicalLatex,
    CalculationInput,
    StembleLatex,
    STextarea,
  },
  mixins: [DynamicQuestionMixin()],
  props: {
    isSubmitting: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      // Put a list of inputs or computed properties here to be sent for marking
      inputs: {
        volH2O2T1: null,
        VfT1: null,
        ViT1: null,
        volTitrantT1: null,
        notesT1: null,
        concH2O2T1: null,

        volH2O2T2: null,
        VfT2: null,
        ViT2: null,
        volTitrantT2: null,
        notesT2: null,
        concH2O2T2: null,

        volH2O2T3: null,
        VfT3: null,
        ViT3: null,
        volTitrantT3: null,
        notesT3: null,
        concH2O2T3: null,

        meanConcH2O2: null,
        stDevConcH2O2: null,

        Tinit: null,
        Tfinal: null,
        deltaT: null,
        volH2O2inRxn: null,
        massCalInit: null,
        massCalFinal: null,
        massSoln: null,

        qSoln: null,
        qCal: null,
        qTotal: null,

        molH2O2: null,
        deltaH: null,

        pctError: null,
      },
    };
  },
};
</script>

<style></style>
